import React, { FC, useEffect, useRef, useState } from 'react'

import SettingsIcon from '@mui/icons-material/Settings'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'

import { CustomButton } from '../../components/CustomButton'
import { useLayoutContext } from '../../components/Layouts/LayoutContext'
import { SelectColumnsModal } from '../../components/Table/SelectColumnsModal'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useElementSize } from '../../utils/useElementSize'
import { useIsMobile } from '../../utils/useIsMobile'

import { OverrideQuestionnaireModal } from './OverrideQuestionnaireModal'
import {
  dismissAllTradesSelect,
  enableMassFill,
  fetchAllTradesInfo,
  selectAllTrades,
} from './TradingLogSlice'

export const TradingLogHeader: FC = () => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()
  const isMobile = useIsMobile('sm')

  const { headerHeight, sidebarWidth } = useLayoutContext()

  const dispatch = useAppDispatch()
  const { itemCount } = useAppSelector((state) => state.tradingLog.pagination)
  const { selectedAll, selectedTrades, trades } = useAppSelector((state) => state.tradingLog)
  const currentAccount = useAppSelector((state) => state.userAccounts.currentAccount)
  const [showColumnsModal, setShowColumnsModal] = useState(false)
  const [showOverrideQuestionnaireModal, setOverrideQuestionnaireModal] = useState(false)

  const tradingLogHeaderRef = useRef<HTMLDivElement>(null)
  const { height: tradingLogHeaderHeight } = useElementSize(tradingLogHeaderRef)

  const handleToggleModal = () => {
    setShowColumnsModal((prev) => !prev)
  }

  const handleCloseQuestionnaireModal = () => {
    setOverrideQuestionnaireModal(false)
  }

  const handleSelect = () => {
    if (!selectedAll) {
      dispatch(selectAllTrades())
    } else {
      dispatch(dismissAllTradesSelect())
    }
  }

  const hasSelectedTrades = selectedTrades?.length > 0

  const checkForFilledJournalingTrades = () => {
    const isFilledJournaling = selectedTrades.some((selectedTrade) => {
      const trade = trades.find((item) => Number(item.id) === selectedTrade.id)
      const journalingCompletion = trade
        ? Number(trade.journalingCompletion)
        : Number(selectedTrade.journalingCompletion)

      return journalingCompletion > 0
    })

    if (isFilledJournaling) {
      setOverrideQuestionnaireModal(true)
    } else {
      dispatch(enableMassFill())
    }
  }

  useEffect(() => {
    if (selectedAll && currentAccount) {
      dispatch(fetchAllTradesInfo(currentAccount.id))
    }
  }, [selectedAll])

  return (
    <>
      <Box
        sx={{
          paddingTop: hasSelectedTrades ? `${tradingLogHeaderHeight}px` : 0,
          transition: 'padding-top 0.3s ease',
        }}
      />

      <Stack
        ref={tradingLogHeaderRef}
        display="flex"
        direction={'row'}
        spacing={2}
        alignItems="center"
        width={'100%'}
        justifyContent={'space-between'}
        padding={spacing(2, 3)}
        bgcolor={palette.background.paper}
        sx={{
          ...(hasSelectedTrades && {
            position: 'fixed',
            top: headerHeight,
            width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
            transition: 'width 0.3s ease',
            right: 0,
            zIndex: 9,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }),
        }}
      >
        {!!itemCount && (
          <>
            <Stack direction={'row'} alignItems={'center'} spacing={2}>
              {hasSelectedTrades && (
                <Typography variant={'caption'}>
                  {t('tradingLogPage.selection.selectedAmountOfTrades', {
                    count: selectedTrades.length,
                  })}
                </Typography>
              )}

              <Button
                variant="text"
                onClick={handleSelect}
                sx={{
                  textTransform: 'inherit',
                  color: palette.blue[500],
                  fontWeight: 'bold',
                }}
              >
                {selectedAll
                  ? t('tradingLogPage.selection.clearSelection', 'Clear selection')
                  : t('tradingLogPage.selection.selectAllTrades', { count: itemCount })}
              </Button>

              {hasSelectedTrades && (
                <CustomButton
                  onClick={checkForFilledJournalingTrades}
                  sx={{
                    fontSize: spacing(1.5),
                    lineHeight: 1,
                    letterSpacing: '0.4px',
                  }}
                >
                  {t('tradingLogPage.selection.journalSelectedTrades')}
                </CustomButton>
              )}
            </Stack>

            <Box
              display="flex"
              justifyContent="center"
              alignItems={'center'}
              gap={spacing(0.5)}
              onClick={handleToggleModal}
              sx={{
                cursor: 'pointer',
                color: palette.grey[700],
                '&:hover': {
                  color: palette.grey[900],
                },
                marginLeft: 'auto',
              }}
            >
              <SettingsIcon color="inherit" />
              {!isMobile && (
                <Typography variant="body1" fontWeight="bold">
                  {t('selectColumnsModal.title', 'Select columns')}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Stack>
      <SelectColumnsModal isOpen={showColumnsModal} closeModal={handleToggleModal} />
      <OverrideQuestionnaireModal
        open={showOverrideQuestionnaireModal}
        onClose={handleCloseQuestionnaireModal}
      />
    </>
  )
}
